import { Download, Pause, Play, RotateCcw, Search, Square, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, downloadFile } from "../api";
import { EmptyState, ExecutionRail, LoadingState, MetricStrip, PageHeader, Pagination, SectionHeading, StatusBadge, TaskTypeIcon, formatDate, readValue, typeLabels } from "../components";
import { useAppData } from "../context";
import type { BatchTaskListItem, TaskDetail } from "../types";

const TASK_DETAIL_PAGE_SIZE = 50;

export function Tasks() {
  const { data } = useAppData();
  const [status, setStatus] = useState("ALL");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const tasks = useMemo(() => data?.tasks.filter((task) => (status === "ALL" || task.status === status) && `${task.taskNo} ${task.note} ${task.creator}`.toLowerCase().includes(query.toLowerCase())) ?? [], [data, query, status]);
  if (!data) return <LoadingState />;
  return <>
    <PageHeader eyebrow="TASKS / ALL" title="批量任务中心" actions={<Link to="/new-task" className="button primary">新建任务</Link>} />
    <section className="workspace-band">
      <div className="filter-bar">
        <label className="search-field"><Search size={16} /><input aria-label="搜索任务" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="任务号、说明、创建人" /></label>
        <select aria-label="筛选状态" value={status} onChange={(event) => setStatus(event.target.value)}><option value="ALL">全部状态</option><option value="RUNNING">执行中</option><option value="VERIFYING">回读中</option><option value="WAITING_APPROVAL">待审批</option><option value="COMPLETED">已完成</option><option value="PAUSED">已暂停</option></select>
        <span className="filter-count">{tasks.length} 个任务</span>
      </div>
      {tasks.length ? <div className="table-scroll"><table className="data-table clickable-rows task-table">
        <thead><tr><th>任务编号</th><th>类型</th><th>状态</th><th>导入 / 可执行</th><th>成功 / 失败 / 复核</th><th>进度</th><th>创建人</th><th>创建时间</th></tr></thead>
        <tbody>{tasks.map((task) => {
          const processed = task.summary.success + task.summary.failed + task.summary.review;
          const percent = task.summary.executable ? Math.round(processed / task.summary.executable * 100) : 100;
          return <tr key={task.id} role="link" tabIndex={0} onClick={() => navigate(`/tasks/${task.id}`)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") navigate(`/tasks/${task.id}`); }}>
            <td><strong className="mono">{task.taskNo}</strong><small>{task.note || "--"}</small></td><td><span className="type-cell"><TaskTypeIcon type={task.taskType} />{typeLabels[task.taskType]}</span></td><td><StatusBadge status={task.status} /></td><td>{task.summary.total} / {task.summary.executable}</td><td><span className="count-positive">{task.summary.success}</span> / <span className="count-negative">{task.summary.failed}</span> / <span className="count-warning">{task.summary.review}</span></td><td><div className="progress-cell"><div><span style={{ width: `${percent}%` }} /></div><strong>{percent}%</strong></div></td><td>{task.creator}</td><td>{formatDate(task.createdAt)}</td>
          </tr>;
        })}</tbody>
      </table></div> : <EmptyState title="暂无真实任务记录" />}
    </section>
  </>;
}

export function TaskDetailPage() {
  const { taskId } = useParams();
  const { data, refresh } = useAppData();
  const [task, setTask] = useState<BatchTaskListItem | null>(null);
  const [details, setDetails] = useState<TaskDetail[]>([]);
  const [totalDetails, setTotalDetails] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [taskLoading, setTaskLoading] = useState(true);
  const [taskError, setTaskError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const requestRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const loadTask = useCallback(async (silent = false) => {
    if (!taskId) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    if (!silent) setTaskLoading(true);
    try {
      const next = await api.task(taskId, detailPage, TASK_DETAIL_PAGE_SIZE, controller.signal);
      if (controller.signal.aborted) return;
      setTask(next.task);
      setDetails(next.details);
      setTotalDetails(next.total);
      setTaskError("");
      setSelectedId((current) => next.details.some((detail) => detail.id === current) ? current : next.details[0]?.id ?? "");
    } catch (error) {
      if (!controller.signal.aborted) setTaskError(error instanceof Error ? error.message : "任务详情加载失败。");
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!controller.signal.aborted) setTaskLoading(false);
    }
  }, [detailPage, taskId]);

  useEffect(() => {
    void loadTask();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void loadTask(true);
    };
    const timer = window.setInterval(refreshWhenVisible, 5000);
    return () => {
      window.clearInterval(timer);
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [loadTask]);

  useEffect(() => {
    setTask(null);
    setDetails([]);
    setDetailPage(1);
    setSelectedId("");
  }, [taskId]);

  if (!data || (taskLoading && !task)) return <LoadingState />;
  if (!task) return <section className="workspace-band"><strong>{taskError || "任务不存在或已被移除。"}</strong></section>;
  const selected = details.find((detail) => detail.id === selectedId) ?? details[0];
  const processed = task.summary.success + task.summary.failed + task.summary.review;
  const progress = task.summary.executable ? Math.round(processed / task.summary.executable * 100) : 100;
  const canControl = data.writeEnabled;
  const canRollback = data.writeEnabled;
  const resolvedTaskId = task.id;

  async function action(name: "pause" | "resume" | "cancel") {
    setBusy(true); setMessage("");
    try { await api.taskAction(resolvedTaskId, name); await Promise.all([refresh(), loadTask(true)]); setMessage("任务状态已更新。" ); }
    catch (error) { setMessage(error instanceof Error ? error.message : "操作失败。" ); }
    finally { setBusy(false); }
  }

  async function rollback() {
    setBusy(true); setMessage("");
    try { const next = await api.rollback(resolvedTaskId); await refresh(); navigate(`/tasks/${next.id}`); }
    catch (error) { setMessage(error instanceof Error ? error.message : "恢复任务创建失败。" ); setBusy(false); }
  }

  return <>
    <PageHeader backTo="/tasks" eyebrow={`${typeLabels[task.taskType]} / ${task.status}`} title={task.taskNo} actions={<>
      <StatusBadge status={task.status} />
      <button className="button secondary" onClick={() => void downloadFile(`/api/tasks/${task.id}/export`, `${task.taskNo}-results.xlsx`)}><Download size={16} />导出结果</button>
      {["RUNNING", "VERIFYING"].includes(task.status) ? <button className="icon-button" disabled={!canControl || busy} title={canControl ? "暂停" : "生产只读模式已锁定"} aria-label="暂停任务" onClick={() => void action("pause")}><Pause size={17} /></button> : null}
      {task.status === "PAUSED" ? <button className="icon-button" disabled={!canControl || busy} title="继续" aria-label="继续任务" onClick={() => void action("resume")}><Play size={17} /></button> : null}
      {["RUNNING", "PAUSED", "WAITING_APPROVAL"].includes(task.status) ? <button className="icon-button danger" disabled={!canControl || busy} title="取消未开始明细" aria-label="取消任务" onClick={() => void action("cancel")}><Square size={16} /></button> : null}
      {task.status === "COMPLETED" ? <button className="button secondary" disabled={!canRollback || busy} title={canRollback ? "创建恢复任务" : "生产只读模式已锁定"} onClick={() => void rollback()}><RotateCcw size={16} />创建恢复任务</button> : null}
    </>} />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    <MetricStrip items={[
      { label: "总体进度", value: `${progress}%`, note: `${processed}/${task.summary.executable} 可执行明细` },
      { label: "成功", value: task.summary.success, tone: "positive" },
      { label: "失败", value: task.summary.failed, tone: task.summary.failed ? "negative" : "" },
      { label: "已拦截", value: task.summary.blocked, tone: task.summary.blocked ? "warning" : "" },
      { label: "待复核", value: task.summary.review, tone: task.summary.review ? "warning" : "" },
    ]} />
    <section className="workspace-band rail-band"><SectionHeading title="执行状态" meta={`${task.creator} · ${formatDate(task.createdAt)}`} /><ExecutionRail tasks={data.tasks} activeTask={task} /></section>
    {task.riskReasons.length ? <div className="risk-banner"><TriangleAlert size={19} /><div><strong>风险标记</strong>{task.riskReasons.map((reason) => <span key={reason}>{reason}</span>)}</div></div> : null}
    <section className="workspace-band task-detail-section">
      <SectionHeading title="任务明细" meta={`${task.summary.total} 条导入记录`} />
      <div className="task-detail-layout">
        <div className="table-scroll"><table className="data-table clickable-rows">
          <thead><tr><th>行 / 批次</th><th>账号 / 站点</th><th>Item ID</th><th>旧值</th><th>目标值</th><th>状态</th><th>错误</th></tr></thead>
          <tbody>{details.map((detail) => <tr key={detail.id} role="button" tabIndex={0} className={selected?.id === detail.id ? "selected" : ""} onClick={() => setSelectedId(detail.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelectedId(detail.id); }}><td className="mono">{detail.rowNo}<small>批次 {detail.batchNo || 1}</small></td><td><strong>{detail.accountCode}</strong><small>{detail.siteId}</small></td><td className="mono">{detail.itemId}</td><td className="value-cell">{readValue(detail.beforeSnapshot)}</td><td className="value-cell">{readValue(detail.targetPayload)}</td><td><StatusBadge status={detail.status} /></td><td>{detail.errorCode ?? detail.issues[0]?.code ?? "--"}</td></tr>)}</tbody>
        </table><Pagination page={detailPage} total={totalDetails} pageSize={TASK_DETAIL_PAGE_SIZE} onChange={(next) => { setDetailPage(next); setSelectedId(""); }} /></div>
        {selected ? <DetailInspector detail={selected} /> : null}
      </div>
    </section>
  </>;
}

function DetailInspector({ detail }: { detail: TaskDetail }) {
  return <aside className="detail-inspector">
    <div className="inspector-head"><div><span>明细 #{detail.rowNo}</span><strong className="mono">{detail.itemId}</strong></div><StatusBadge status={detail.status} /></div>
    <dl><div><dt>幂等键</dt><dd className="mono">{detail.idempotencyKey.slice(0, 18)}…</dd></div><div><dt>提交重试</dt><dd>{detail.retryCount}</dd></div><div><dt>回读值</dt><dd>{detail.afterSnapshot ? readValue(detail.afterSnapshot) : "尚无回读"}</dd></div></dl>
    {detail.issues.length ? <div className="issue-list">{detail.issues.map((issue) => <div key={`${issue.code}-${issue.message}`}><strong>{issue.code}</strong><span>{issue.message}</span>{issue.excerpt ? <code>{issue.excerpt}</code> : null}</div>)}</div> : null}
    <SectionHeading title="API 尝试" meta={`${detail.attempts.length} 条`} />
    <div className="attempt-list">{detail.attempts.length ? [...detail.attempts].reverse().map((attempt) => <div key={attempt.id}><span className={`attempt-kind ${attempt.kind.toLowerCase()}`}>{attempt.kind}</span><div><strong>{attempt.httpStatus ?? "--"} · {attempt.durationMs}ms</strong><small>{attempt.summary}</small><code>{attempt.requestId}</code></div></div>) : <span className="muted-text">尚未调用平台适配器</span>}</div>
  </aside>;
}
